import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    Input,
    Select,
    SimpleGrid,
    Switch,
    Tag,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { translate } from '../../../i18n';
import { Currency } from '../../../models/court-pricing/dto/court-pricing-rule.dto';
import { SportType } from '../../../models/court/dto/court.dto';
import { getChartPalette } from '../../../theme/chart-config';
import { typography } from '../../../theme/tokens';
import { currencyEnumToSymbol } from '../../court-pricing-management/components/CurrencyPicker';
import { PricingBlock, blockDurations, formatMinutes, parseTimeToMinutes } from '../model';
import { usePricingGridState } from '../usePricingGridState';
import { countGridCells } from '../utils/build-grid-compact-blocks';
import PricingBlocksEditor from './PricingBlocksEditor';
import PricingGrid from './PricingGrid';

export type CourtPricingGridEditorProps = {
    grid: ReturnType<typeof usePricingGridState>;
    sports: SportType[];
    currency: Currency;
    returnAsOneChunk: boolean;
    onReturnAsOneChunkChange: (next: boolean) => void;
    startDate: Date;
    endDate: Date;
    onStartDateChange: (next: Date) => void;
    onEndDateChange: (next: Date) => void;
    /** Shown above the grid, e.g. "prices come from the week of …". */
    notice?: ReactNode;
};


function toInputDate(date: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

function fromInputDate(value: string, fallback: Date): Date {
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return fallback;
    return new Date(y, m - 1, d, fallback.getHours(), fallback.getMinutes(), fallback.getSeconds(), fallback.getMilliseconds());
}

export function formatDateLabel(date: Date): string {
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Which duration to show: the previous one if a block still has it, else the first. */
function useSelectedDuration(durations: number[]) {
    const [duration, setDuration] = useState<number | undefined>(durations[0]);
    useEffect(() => {
        if (duration === undefined || !durations.includes(duration)) setDuration(durations[0]);
    }, [durations, duration]);
    return [duration, setDuration] as const;
}

/** Which selected sport to show: the previous one if still offered, else the first. */
function useSelectedSport(sports: SportType[]) {
    const [sport, setSport] = useState<SportType | undefined>(sports[0]);
    useEffect(() => {
        if (sport === undefined || !sports.includes(sport)) setSport(sports[0]);
    }, [sports, sport]);
    return [sport, setSport] as const;
}

/**
 * Blocks + weekday × slot grid, the new way to price a court. Used by the
 * create-court modal and by the court-details edit modal; the owner holds
 * the dates, the sports and the "one chunk" flag because they are part of
 * the payload it sends, while the grid itself lives in `usePricingGridState`.
 */
export default function CourtPricingGridEditor({
    grid, sports, currency, returnAsOneChunk, onReturnAsOneChunkChange,
    startDate, endDate, onStartDateChange, onEndDateChange, notice,
}: CourtPricingGridEditorProps) {
    const { state, slots, updateBlock, addBlock, removeBlock, applyPrice } = grid;
    const [sport, setSport] = useSelectedSport(sports);
    const durations = useMemo(() => blockDurations(state.blocks), [state.blocks]);
    const [duration, setDuration] = useSelectedDuration(durations);
    const visibleSlots = useMemo(
        () => slots.filter((slot) => slot.endMinute - slot.startMinute === duration),
        [slots, duration],
    );
    const palette = useMemo(() => getChartPalette(), []);
    const currencySymbol = currencyEnumToSymbol(currency);
    const mutedText = useColorModeValue('gray.500', 'gray.500');
    const legendBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

    const blockColorFor = (blockId: string) => {
        const index = state.blocks.findIndex((b) => b.id === blockId);
        return palette[(index < 0 ? 0 : index) % palette.length];
    };

    const counts = countGridCells(state, sports);

    const legend = (block: PricingBlock) => {
        const start = parseTimeToMinutes(block.start);
        const end = parseTimeToMinutes(block.end);
        const hours = Number.isNaN(start) || Number.isNaN(end) ? '—' : `${formatMinutes(start)}–${formatMinutes(end)}`;
        return `${hours} · ${block.durationMinutes}${translate('courts.pricing_grid.minutes_short')} / ${block.intervalMinutes}${translate('courts.pricing_grid.minutes_short')}`;
    };

    return (
        <Flex direction="column" gap={6}>
            {/* The period is the ONE thing that decides which weeks a save
                touches, so it is a labelled section of its own — not a pill
                with week arrows, which read as "browse weeks" and silently
                moved the period instead. The grid below is a weekly template
                applied to every week in this period. */}
            <Box>
                <Text {...typography.formSectionLabel} color={mutedText} mb={3}>
                    {translate('courts.pricing_grid.period_title')}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                    <FormControl isInvalid={endDate < startDate}>
                        <FormLabel {...typography.formLabel}>{translate('courts.pricing_grid.period_start')}</FormLabel>
                        <Input type="date" value={toInputDate(startDate)} onChange={(e) => onStartDateChange(fromInputDate(e.target.value, startDate))} />
                    </FormControl>
                    <FormControl isInvalid={endDate < startDate}>
                        <FormLabel {...typography.formLabel}>{translate('courts.pricing_grid.period_end')}</FormLabel>
                        <Input type="date" value={toInputDate(endDate)} onChange={(e) => onEndDateChange(fromInputDate(e.target.value, endDate))} />
                    </FormControl>
                </SimpleGrid>
                <Text {...typography.caption} color={mutedText} mt={2}>
                    {translate('courts.pricing_grid.period_hint', { start: formatDateLabel(startDate), end: formatDateLabel(endDate) })}
                </Text>
            </Box>

            <FormControl display="flex" alignItems="center" justifyContent="space-between" gap={4}>
                <Box>
                    <FormLabel {...typography.formLabel} mb={0}>{translate('courts.return_as_one_chunk')}</FormLabel>
                    <FormHelperText mt={1}>{translate('courts.pricing_grid.single_chunk_hint')}</FormHelperText>
                </Box>
                <Switch colorScheme="brand" isChecked={returnAsOneChunk} onChange={(e) => onReturnAsOneChunkChange(e.target.checked)} />
            </FormControl>

            <Box>
                <Text {...typography.formSectionLabel} color={mutedText} mb={3}>
                    {translate('courts.pricing_grid.blocks_title')}
                </Text>
                <PricingBlocksEditor
                    blocks={state.blocks}
                    currencySymbol={currencySymbol}
                    blockColorFor={blockColorFor}
                    onChange={updateBlock}
                    onAdd={addBlock}
                    onRemove={removeBlock}
                />
            </Box>

            <Box>
                <Text {...typography.formSectionLabel} color={mutedText} mb={3}>
                    {translate('courts.pricing_grid.grid_title')}
                </Text>

                <Flex align="center" justify="space-between" gap={3} wrap="wrap" mb={3}>
                    <HStack spacing={2} wrap="wrap">
                        {state.blocks.map((block) => (
                            <Tag key={block.id} size="sm" bg={legendBg} borderRadius="full" gap={2}>
                                <Box w="9px" h="9px" borderRadius="full" bg={blockColorFor(block.id)} />
                                {legend(block)}
                            </Tag>
                        ))}
                    </HStack>

                    <HStack spacing={3} wrap="wrap">
                        <Select
                            size="sm"
                            w="auto"
                            minW="120px"
                            aria-label={translate('courts.pricing_grid.duration_label')}
                            value={duration ?? ''}
                            isDisabled={durations.length <= 1}
                            onChange={(e) => setDuration(Number(e.target.value))}
                        >
                            {durations.map((d) => (
                                <option key={d} value={d}>{d} {translate('courts.pricing_grid.minutes_short')}</option>
                            ))}
                        </Select>
                        <Select
                            size="sm"
                            w="auto"
                            minW="140px"
                            aria-label={translate('courts.pricing_grid.sport_label')}
                            value={sport ?? ''}
                            isDisabled={sports.length === 0}
                            onChange={(e) => setSport(e.target.value as SportType)}
                        >
                            {sports.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </Select>
                    </HStack>
                </Flex>

                {notice}

                {sports.length === 0 || sport === undefined ? (
                    <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription fontSize="sm">{translate('courts.pricing_grid.no_sport')}</AlertDescription>
                    </Alert>
                ) : (
                    <>
                        <Text {...typography.caption} color={mutedText} mb={2}>
                            {translate('courts.pricing_grid.grid_hint')}
                        </Text>
                        {durations.length > 1 && (
                            <Text {...typography.caption} color={mutedText} mb={2}>
                                {translate('courts.pricing_grid.duration_hint', { duration: duration ?? '' })}
                            </Text>
                        )}
                        <PricingGrid
                            state={state}
                            slots={visibleSlots}
                            sport={sport}
                            currencySymbol={currencySymbol}
                            blockColorFor={blockColorFor}
                            onApplyPrice={(day, targetSlots, price) => applyPrice(sport, day, targetSlots, price)}
                        />
                        <Text {...typography.caption} color={mutedText} mt={2}>
                            {translate('courts.pricing_grid.summary', { priced: counts.priced, unpriced: counts.unpriced })}
                        </Text>
                    </>
                )}
            </Box>
        </Flex>
    );
}
